<template>
  <div class="kt-form kt-form--label-right">

    <div class="row">
      <div class="form-group col-md-4 validated px-5">
        <BaseSelect
          label="Brand"
          :options="opportunityDetails.brands"
          :isMandatory="$v[validationGroupName].opportunityDetailsTab.brandId.required"
          v-model="$v[validationGroupName].opportunityDetailsTab.brandId.$model"
          @blur="$v[validationGroupName].opportunityDetailsTab.brandId.$touch()"
          :validator="$v[validationGroupName].opportunityDetailsTab.brandId"
          @input="onBrandChange"
          :disabled="isCompletedQuote" />
      </div>
      <div class="form-group col-md-4 validated px-5">
        <BaseRadioButtonGroup
          label="Type"
          :options="opportunityTypes"
          :isMandatory="true"
          v-model="$v[validationGroupName].opportunityDetailsTab.typeId.$model"
          @blur="$v[validationGroupName].opportunityDetailsTab.typeId.$touch()"
          :validator="$v[validationGroupName].opportunityDetailsTab.typeId"
          :disabled="isCompletedQuote" />
        <template v-if="$v[validationGroupName].opportunityDetailsTab.typeId.$error">
          <div class="invalid-feedback" v-if="!$v[validationGroupName].opportunityDetailsTab.typeId.required">
            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-1" /> Type is required.</div>
        </template>
      </div>
      <div class="form-group col-md-4 validated px-5">
        <label class="labelTitle">Departure date</label>
        <Datepicker
          :value="departureDate"
          @opened="$v[validationGroupName].opportunityDetailsTab.departureDate.$touch()"
          input-class="form-control"
          placeholder="DD/MM/YYYY"
          format="dd/MM/yyyy"
          :bootstrap-styling="true"
          :disabled-dates="departureDateConfig.disabledDates"
          :highlighted="departureDateConfig.highlighted"
          @selected="onSelected"
          :disabled="isCompletedQuote" />
      </div>
    </div>

    <div class="row">
      <div class="form-group col-md-4 validated px-5 reference_number_txt">
        <BaseInputForm
          type="text"
          label="Reference number"
          placeholder="Add it only when the itinerary is complete."
          v-model="$v[validationGroupName].opportunityDetailsTab.referenceNumber.$model"
          @blur="$v[validationGroupName].opportunityDetailsTab.referenceNumber.$touch()"
          :validator="$v[validationGroupName].opportunityDetailsTab.referenceNumber"
          :disabled="isCompletedQuote" />
        <template v-if="$v[validationGroupName].opportunityDetailsTab.referenceNumber.$error">
          <div
            class="invalid-feedback"
            v-if="!$v[validationGroupName].opportunityDetailsTab.referenceNumber.validateReferenceNumber">
            This reference number already exist.
          </div>
        </template>
      </div>
      <div class="form-group col-md-4 validated px-5">
        <BaseInputForm
          type="text"
          label="Opportunity name"
          placeholder="Opportunity name goes here"
          v-model="$v[validationGroupName].opportunityDetailsTab.opportunityName.$model"
          @blur="$v[validationGroupName].opportunityDetailsTab.opportunityName.$touch()"
          :validator="$v[validationGroupName].opportunityDetailsTab.opportunityName"
          :disabled="isCompletedQuote" />
      </div>
      <div class="form-group col-md-4 validated px-5">
        <BaseSelect
          label="Region"
          :options="opportunityDetails.regions"
          :isMandatory="true"
          v-model="$v[validationGroupName].opportunityDetailsTab.regionId.$model"
          @blur="$v[validationGroupName].opportunityDetailsTab.regionId.$touch()"
          :validator="$v[validationGroupName].opportunityDetailsTab.regionId"
          :disabled="isCompletedQuote" />
        <template v-if="$v[validationGroupName].opportunityDetailsTab.regionId.$error">
          <div class="invalid-feedback" v-if="!$v[validationGroupName].opportunityDetailsTab.regionId.required">
            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-1" /> Region is required.</div>
        </template>
      </div>
      <div v-if="canChangeOwner" class="form-group col-md-4 validated owner-input px-5">
        <BaseInputForm
          type="text"
          label="Owner"
          placeholder="Select owner"
          v-model="$v[validationGroupName].opportunityDetailsTab.ownerName.$model"
          @blur="$v[validationGroupName].opportunityDetailsTab.ownerName.$touch()"
          :validator="$v[validationGroupName].opportunityDetailsTab.ownerName"
          @click="showModal = true"
          :disabled="isCompletedQuote" />
      </div>
    </div>
    <Portal to="deletePortal">
      <UserSelectorModal
        :show="showModal"
        :user-id="this.$v[this.validationGroupName].opportunityDetailsTab.ownerId.$model"
        :users="opportunityDetails.users"
        :select-user="selectOwner"
        :close-modal="() => showModal = false"
      />
    </Portal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Datepicker from 'vuejs-datepicker'
import { formatDate, parseDate } from '@/helpers'
import UserSelectorModal from "@/components/opportunity-details/UserSelectorModal.vue";

export default {
  name: 'OpportunityDetailsTab',
  inject: ['$v'],
  components: {
    UserSelectorModal,
    Datepicker
  },
  data() {
    return {
      showModal: false,
    };
  },
  beforeMount () {
    if (!this.data) {
      this.$v[this.validationGroupName].opportunityDetailsTab.ownerId.$model = this.opportunityDetails.owner
      this.$v[this.validationGroupName].opportunityDetailsTab.regionId.$model = this.opportunityDetails.regions.length === 1 ? this.opportunityDetails.regions[0].value : ''
    }

    if (this.data) {
      const form = {
        opportunityDetailsTab: {
          typeId: this.data.properties_opportunity.id_opportunity_export_type,
          brandId: this.data.properties_opportunity.id_brand,
          opportunityName: this.data.opportunity_name,
          ownerName: this.data.firstname_owner + " " + this.data.lastname_owner,
          referenceNumber: this.data.reference_number,
          departureDate: this.data.departure_date,
          ownerId: this.data.id_owner,
          regionId: this.data.selling_regions ? this.data.selling_regions[0].id : null
        },
        clientInformationTab: {
          typeId: this.data.client_details.properties_client.id_type_client,
          accountName: this.data.client_details.account_name,
          email: this.data.client_details.account_email,
          phoneNumber: this.data.client_details.phone_number,
          address: this.data.client_details.account_address,
          contactName: this.data.client_details.contact_name
        },
        quoteRequestTab: {
          supplierId: this.data.request_form.properties_request_form.id_supplier,
          stageId: this.data.request_form.properties_request_form.id_stage_supplier,
          hotelStandard: this.data.request_form.hotel_standard,
          focPlaces: this.data.request_form.foc_places,
          budget: this.data.request_form.budget ? this.data.request_form.budget : '',
          priceBreak: this.data.request_form.price_break,
          guests: this.data.request_form.properties_request_form.id_guests,
          commission: this.data.request_form.percentage_commission,
          arrivalAirportPort: this.data.request_form.airport_arrive,
          departureAirportPort: this.data.request_form.airport_departure,
          quoteDeadline: this.data.request_form.quote_deadline,
          difficultDatesChecked: this.data.request_form.difficult_dates_checked,
          flourishesLocalTreats: this.data.request_form.flourishes_local_treats,
          gratuitiesForTdDriver: this.data.request_form.gratuities_for_td_driver,
          tdOfferOptionals: this.data.request_form.td_offer_optionals,
          prebookingsRequired: this.data.request_form.prebookings_required,
          repeatGuestGift: this.data.request_form.repeat_guest_gift,
          includingLocalGuides: this.data.request_form.including_local_guides,
          birthdayAllowance: this.data.request_form.birthday_allowance,
          groupPhoto: this.data.request_form.group_photo,
          additionalInformation: this.data.request_form.additional_information,
          additionalFileId: this.data.request_form.properties_request_form.id_additional_doc
        },
        sections: {
          letter: this.data.itineraries[this.itinerarySourceTypeSelected.index].letter_itinerary,
          pricing: this.data.itineraries[this.itinerarySourceTypeSelected.index].pricing_itinerary,
          agentPricing: this.data.itineraries[this.itinerarySourceTypeSelected.index].pricing_agent_itinerary,
          termsAndConditions: this.data.itineraries[this.itinerarySourceTypeSelected.index].terms_conditions,
          consumerTermsAndConditions: this.data.itineraries[this.itinerarySourceTypeSelected.index].consumer_terms_conditions,
          bookMyGroup: this.data.itineraries[this.itinerarySourceTypeSelected.index].book_group,
          airContract: this.data.itineraries[this.itinerarySourceTypeSelected.index].air_contract,
        },
        consumerSection: {
          yourDetails: {
            callToAction: (this.data.consumer_section && this.data.consumer_section.call_action) ? this.data.consumer_section.call_action : '',
            uploadCompanyLogo: '',
            uploadTourHostPhoto: '',
            tourHostName: this.data.consumer_section ? this.data.consumer_section.tour_host_name : '',
            tourHostPosition: this.data.consumer_section ? this.data.consumer_section.tour_host_position : ''
          },
          tripDetails: {
            tripName: this.data.consumer_section ? this.data.consumer_section.trip_name : '',
            tripSubtitle: this.data.consumer_section ? this.data.consumer_section.trip_subtitle : '',
            companyName: this.data.consumer_section ? this.data.consumer_section.company_name : '',
            twinsharePrice: this.data.consumer_section ? this.data.consumer_section.twinshare_price : '',
            singleSuppPrice: this.data.consumer_section ? this.data.consumer_section.single_supp_price : '',
            twinsharePriceName: this.data.consumer_section ? this.data.consumer_section.twinshare_price_name : 'Twin Share Price',
            singleSuppPriceName: this.data.consumer_section ? this.data.consumer_section.single_supp_price_name : 'Single Share Price',
            shortDescription: (this.data.consumer_section && this.data.consumer_section.short_description) ? this.data.consumer_section.short_description : '',
            termsAndConditions: (this.data.consumer_section && this.data.consumer_section.terms_conditions) ? this.data.consumer_section.terms_conditions : ''
          }
        },
        guestDocumentation: {
          introduction: this.data.guest_documentation ? this.data.guest_documentation.introduction : '',
          optionalExperience: this.data.guest_documentation ? this.data.guest_documentation.optional_experience : '',
          hotelList: this.data.guest_documentation ? this.data.guest_documentation.hotel_list : ''
        }
      }
      this.$v[this.validationGroupName].$model = form
    }
  },
  computed: {
    ...mapGetters({
      'opportunityDetails': 'opportunity/opportunityDetails',
      itinerarySourceTypeSelected: 'opportunity/itinerarySourceTypeSelected'
    }),
    opportunityTypes () {
      const brandId = parseInt(this.$v[this.validationGroupName].opportunityDetailsTab.brandId.$model);
      return this.opportunityDetails.types.filter((type) => type.brands.includes(brandId));
    },
    departureDate () {
      const date = this.$v[this.validationGroupName].opportunityDetailsTab.departureDate.$model
      if (date) {
        return parseDate(date)
      }
      return date
    },
    canChangeOwner() {
      return this.data;
    },
    departureDateConfig () {
      return {
        disabledDates: {
          to: new Date()
        },
        highlighted: {
          dates: [ new Date() ],
          includeDisabled: true
        }
      }
    }
  },
  props: {
    validationGroupName: {
      type: String,
      required: false
    },
    data: {
      type: Object,
      required: false
    },
    isCompletedQuote: {
      type: Boolean,
      required: false
    }
  },
  methods: {
    selectOwner (user) {
      this.showModal = false
      this.$v[this.validationGroupName].opportunityDetailsTab.ownerId.$model = user.id
      this.$v[this.validationGroupName].opportunityDetailsTab.ownerName.$model = user.firstname + " " + user.lastname;
    },
    onSelected (dateSalected) {
      const formattedDate = formatDate(dateSalected)
      this.$v[this.validationGroupName].opportunityDetailsTab.departureDate.$model = formattedDate
    },
    onBrandChange () {
      this.$nextTick(() => {
        this.$v[this.validationGroupName].opportunityDetailsTab.typeId.$model = this.opportunityTypes[0].value;
      });
    },
  }
}
</script>

<style>
  .owner-input input {
    cursor: pointer;
  }
</style>
